/**
 * The clarify gate, on the client.
 *
 * The backend decides — deterministically, before anything is metered —
 * whether a query can run or names a variable it genuinely cannot fill. When
 * it cannot, the turn comes back as a QUESTION rather than an answer, and it
 * is durable: the user can close the tab and answer it next week.
 *
 * This module is the shape of that exchange plus the pure functions over it.
 * Nothing here fetches; the hook owns the requests and the components own the
 * pixels, so the rules that decide whether an answer set is submittable are
 * testable without either.
 */

/**
 * An option the user may pick.
 *
 * `kind` says what the value MEANS, which is the whole of the answer grammar
 * on the client:
 *
 *   `option`   — a plain slot value (`NO`, `memo`).
 *   `reframe`  — `reframe:<slug>`. Not a slot value at all: it rewrites the
 *                question ("answer generally about non-compete clauses"), and
 *                the backend states it as the clarification line so the answer
 *                visibly says which question it answered (§4.6, R5).
 *   `locator`  — `upload:{uuid}#{chunk}` or `matter_doc:{filename}`, a handle
 *                the pipeline can actually retrieve against.
 *
 * §4.3: an option is offered only if its value resolves to something real.
 * The engine validates that before it emits; nothing here invents one.
 */
export interface ClarifyOption {
  value: string;
  label: string;
  hint?: string;
  kind?: 'option' | 'reframe' | 'locator' | string;
  /** `deterministic` (built by the gate) or `model` (Layer 2 authored). */
  source?: 'deterministic' | 'model' | string;
}

/**
 * An affordance beside the question — NOT an answer value (§2.5).
 *
 * `attach_document` opens the file picker; `link_matter` opens the matter
 * link. They exist for the cardinality-0 case, where offering options would
 * mean inventing them: the honest move is to let the user supply the thing the
 * question is about rather than guess at its contents.
 */
export type ClarifyActionKind = 'attach_document' | 'link_matter' | string;

export interface ClarifyAction {
  kind: ClarifyActionKind;
  label?: string;
  hint?: string;
}

/** One question, exactly as `research_gate` / Layer 2 emits it. */
export interface ClarifyQuestion {
  /** Stable and rule-scoped (`h1_jurisdiction`). The answer keys off this. */
  id: string;
  slot: string;
  kind: 'single_choice' | 'multi_choice' | 'free_text' | 'date' | string;
  prompt: string;
  /** BCP-47 of the prompt, which is the session's output language, not the UI's. */
  prompt_language?: string;
  options?: ClarifyOption[];
  actions?: ClarifyAction[];
  /**
   * Pre-selected. Plan §3.1: "Enter runs" — the default set is always
   * executable, so a question is never a wall.
   */
  default_value?: string;
  /** Every choice question also accepts prose. */
  allow_free_text?: boolean;
  /** Cardinality 0: the prompt says the set is empty and this says what to type. */
  free_text_placeholder?: string;
  required?: boolean;
  /** What the gate saw that made it ask ("this clause"). Diagnostic only. */
  detected?: string;
}

/** What the gate is about to do, stated before any work starts. */
export interface ResearchPlan {
  version?: number;
  action?: 'run' | 'clarify' | 'confirm' | string;
  reason?: string;
  format?: string;
  format_source?: string;
  depth?: string;
  scope_branch?: string;
  jurisdictions?: string[];
  jurisdiction_source?: string;
  scope_size?: number;
  uploads_only?: boolean;
  has_matter?: boolean;
  resolved?: Record<string, string>;
  /** Every slot filled by a default rather than by the user, named out loud. */
  assumptions?: string[];
}

/** An outstanding question set, as `GET /pending` returns it. */
export interface PendingPlan {
  status: string;
  /**
   * 1 = the provisional question the deterministic gate wrote, 2 = the
   * authored one that replaced it.
   *
   * The user may see both, in that order, on one card: the 200 carries the
   * provisional immediately and `research.clarify` upgrades it in place a
   * moment later (R4). The layer is what tells them apart.
   */
  layer?: number;
  message_uuid: string;
  correlation_id: string;
  sequence_number?: number | null;
  /** H1..H5 — which enumerated rule asked. */
  trigger?: string | null;
  round: number;
  max_rounds: number;
  questions: ClarifyQuestion[];
  /** Answers from an earlier round, so round two never re-asks round one. */
  answers?: Record<string, string>;
  plan?: ResearchPlan;
  resolved?: Record<string, string>;
  query?: string;
  expires_at?: string | null;
  created_at?: string | null;
}

/** The clarification state carried on the assistant turn it belongs to. */
export interface TurnClarification {
  pending: PendingPlan;
  /** What the user has typed or picked, not yet submitted. */
  draft: Record<string, string>;
  submitting?: boolean;
  error?: string;
  /**
   * Answered, and the run has taken over.
   *
   * The card collapses to a compact "asked and answered" strip rather than
   * disappearing, so the transcript records what was assumed on the user's
   * behalf. A conversation that silently forgets what it asked is one nobody
   * can audit.
   */
  settled?: boolean;
}

export const AWAITING_CLARIFICATION = 'awaiting_clarification';

/** Did the send come back as a question rather than as an accepted run? */
export function isClarifyResponse(result: any): boolean {
  return String(result?.status || '') === AWAITING_CLARIFICATION;
}

/**
 * A send response, turned into the pending shape the resume path also produces.
 *
 * ONE SHAPE for "we just asked" and "we reopened a chat that had been asked",
 * so the card renders from a single code path in both cases. Getting this
 * wrong is how the two states come to look subtly different on screen.
 */
export function pendingFromSendResult(result: any): PendingPlan {
  return {
    status: AWAITING_CLARIFICATION,
    message_uuid: String(result?.message_uuid || ''),
    correlation_id: String(result?.correlation_id || ''),
    sequence_number: result?.sequence_number ?? null,
    trigger: result?.trigger ?? null,
    round: Number(result?.round ?? 1),
    max_rounds: Number(result?.max_rounds ?? 2),
    questions: Array.isArray(result?.questions) ? result.questions : [],
    answers: result?.answers || {},
    plan: result?.plan || {},
    query: result?.query || '',
    layer: Number(result?.layer ?? 1),
    expires_at: result?.expires_at ?? null,
  };
}

/**
 * The answer set a question opens with — its defaults, already filled in.
 *
 * Plan §3.1: "Enter runs. The default set is always executable." A question
 * that opens empty is a form; a question that opens with a sensible answer
 * pre-selected is a confirmation the user can accept or override.
 */
export function initialDraft(pending: PendingPlan): Record<string, string> {
  const draft: Record<string, string> = { ...(pending.answers || {}) };
  for (const question of pending.questions || []) {
    if (draft[question.id] !== undefined) continue;
    if (question.default_value) draft[question.id] = question.default_value;
  }
  return draft;
}

/** Only non-blank answers travel. A blank is not an answer. */
export function answersToSubmit(draft: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(draft || {})) {
    const trimmed = String(value ?? '').trim();
    if (trimmed) out[key] = trimmed;
  }
  return out;
}

/**
 * Can this be submitted?
 *
 * A REQUIRED question with no answer blocks. Everything else runs — including
 * a round where the user answered nothing at all, because "run with what you
 * have" is a legitimate choice and the gate states the defaults it used.
 */
export function canSubmit(
  pending: PendingPlan,
  draft: Record<string, string>
): boolean {
  const answers = answersToSubmit(draft);
  return (pending.questions || []).every(
    (question) => !question.required || !!answers[question.id]
  );
}

/**
 * Is every required question already answered by its default?
 *
 * Drives the "Run with defaults" affordance: it is offered only when it would
 * actually produce a run, never as a button that turns out to be refused.
 */
export function defaultsAreExecutable(pending: PendingPlan): boolean {
  return canSubmit(pending, initialDraft(pending));
}

/** The one-line summary the settled strip shows. */
export function answeredSummary(
  pending: PendingPlan,
  answers: Record<string, string>
): string {
  const parts = (pending.questions || [])
    .map((question) => {
      const value = answers[question.id] || answers[question.slot];
      if (!value) return '';
      // A multi-select answer is several values in one string; each is named
      // by its own label, so the record reads as what was picked rather than
      // as the wire format it travelled in.
      const label = selectedValues(value)
        .map((one) => {
          const option = (question.options || []).find((o) => o.value === one);
          return option?.label || one;
        })
        .join(', ');
      return `${question.slot.replace(/_/g, ' ')}: ${label}`;
    })
    .filter(Boolean);
  return parts.join(' · ');
}

/** Has the gate run out of rounds? After the last one it runs with defaults. */
export function isFinalRound(pending: PendingPlan): boolean {
  return Number(pending.round || 1) >= Number(pending.max_rounds || 2);
}

/**
 * Attach a pending question to the turn it belongs to.
 *
 * Matched on `message_uuid`, NOT appended to the end, and that is the whole
 * resume behaviour in one line: the question renders in its sequence position,
 * so a user who reopens a chat sees the conversation exactly where they left
 * it with the question still live — rather than a card pinned to the bottom
 * under three later turns that, by construction, cannot exist.
 *
 * A pending plan whose turn is not on this page is dropped rather than
 * force-fitted. It will attach when that page loads; inventing a turn for it
 * would put an answer-less question somewhere it never was.
 */
export function withPending<
  T extends { kind: string; messageUuid?: string }
>(turns: T[], pending: PendingPlan | null): T[] {
  if (!pending?.message_uuid) return turns;
  return turns.map((turn) =>
    turn.kind === 'assistant' && turn.messageUuid === pending.message_uuid
      ? {
          ...turn,
          awaitingClarification: true,
          live: undefined,
          clarification: { pending, draft: initialDraft(pending) },
        }
      : turn
  );
}

/* ------------------------------------------------------------------ */
/*  The answer grammar                                                 */
/* ------------------------------------------------------------------ */

/**
 * Several picks in one answer string.
 *
 * The answer map is `Record<string, string>` on both sides of the wire, so a
 * multi-select has to travel as one value. Comma, no space: the values it
 * carries are ISO codes, enum members and locators, none of which contain one.
 */
export const MULTI_SEPARATOR = ',';

/** The picks inside an answer value. A single-select is a list of one. */
export function selectedValues(value: string | undefined): string[] {
  return String(value ?? '')
    .split(MULTI_SEPARATOR)
    .map((part) => part.trim())
    .filter(Boolean);
}

/**
 * Add or remove one pick.
 *
 * Single-select REPLACES — and a second click on the chosen chip does not
 * clear it, because a required question with nothing chosen is a worse state
 * than the one the user was in. Multi-select toggles, and preserves the order
 * the options were offered in rather than click order, so the same set always
 * renders the same way.
 */
export function toggleValue(
  current: string | undefined,
  option: string,
  multi: boolean,
  order?: string[]
): string {
  if (!multi) return option;
  const chosen = new Set(selectedValues(current));
  if (chosen.has(option)) chosen.delete(option);
  else chosen.add(option);
  const sequence = order && order.length ? order : Array.from(chosen);
  const ordered = sequence.filter((value) => chosen.has(value));
  for (const value of chosen) if (!ordered.includes(value)) ordered.push(value);
  return ordered.join(MULTI_SEPARATOR);
}

/** Is this question answered by picking rather than by typing? */
export function isChoice(question: ClarifyQuestion): boolean {
  return (question.options || []).length > 0;
}

export function isMultiChoice(question: ClarifyQuestion): boolean {
  return question.kind === 'multi_choice';
}

/** `reframe:non_compete` — answer the general question instead of this one. */
export function isReframe(value: string): boolean {
  return String(value || '').startsWith('reframe:');
}

export function reframeSlug(value: string): string {
  return isReframe(value) ? String(value).slice('reframe:'.length) : '';
}

/**
 * A handle the pipeline can retrieve against, rather than prose about one.
 *
 * `upload:{uuid}#{chunk}` narrows to a chunk of an attached file;
 * `matter_doc:{filename}` to a document on the linked matter. §2.7: the
 * backend attaches these to the enqueue payload as a SCOPE NARROWING, never
 * concatenated into the query string.
 */
export function isLocator(value: string): boolean {
  const text = String(value || '');
  return text.startsWith('upload:') || text.startsWith('matter_doc:');
}

export function uploadLocator(uuid: string, chunk?: string | number): string {
  return chunk === undefined || chunk === null || chunk === ''
    ? `upload:${uuid}`
    : `upload:${uuid}#${chunk}`;
}

export function matterDocLocator(filename: string): string {
  return `matter_doc:${filename}`;
}

/* ------------------------------------------------------------------ */
/*  The upgrade — `research.clarify`                                   */
/* ------------------------------------------------------------------ */

/** The `research.clarify` event, as §2.5 pins it. */
export interface ClarifyEvent {
  type?: string;
  chat_id?: string;
  message_uuid?: string;
  correlation_id?: string;
  layer?: number;
  trigger?: string;
  round?: number;
  max_rounds?: number;
  blocking?: boolean;
  plan?: ResearchPlan;
  questions?: ClarifyQuestion[];
  answer_endpoint?: string;
  expires_at?: string | null;
}

export const CLARIFY_EVENT = 'research.clarify';

export function isClarifyEvent(eventName: string): boolean {
  return eventName === CLARIFY_EVENT;
}

/**
 * Is this event about the question currently on screen?
 *
 * Correlation-filtered like every other `research.*` event, and message-uuid
 * filtered on top: one chat can hold one live question, but a stale event from
 * a turn the user abandoned must not rewrite the one they are looking at.
 */
export function clarifyEventMatches(
  event: ClarifyEvent | null | undefined,
  pending: PendingPlan | null | undefined
): boolean {
  if (!event || !pending) return false;
  const payload: any = event;
  const correlation = payload.correlation_id || payload.correlationId;
  if (correlation && pending.correlation_id && correlation !== pending.correlation_id) {
    return false;
  }
  const message = payload.message_uuid || payload.messageUuid;
  if (message && pending.message_uuid && message !== pending.message_uuid) return false;
  return !!(correlation || message);
}

/**
 * The authored question, put where the provisional one was.
 *
 * THE ROUND DOES NOT ADVANCE (§3.1). The upgrade is the same question said
 * better — spending one of two rounds on phrasing would be the gate asking
 * twice for one answer. The card is replaced in place; a second card is never
 * stacked, because there is only ever one pending plan.
 *
 * An event with no questions is a no-op rather than an erasure: whatever went
 * wrong upstream, the provisional question is still answerable and is what the
 * user gets (§2.6 rule 6).
 */
export function applyClarifyUpgrade(
  pending: PendingPlan,
  event: ClarifyEvent
): PendingPlan {
  const questions = Array.isArray(event.questions) ? event.questions : [];
  if (!questions.length) return pending;
  // A LOWER layer never overwrites a higher one — a provisional arriving late
  // must not undo the authored question already on screen.
  if (Number(event.layer ?? 2) < Number(pending.layer ?? 1)) return pending;
  return {
    ...pending,
    layer: Number(event.layer ?? 2),
    trigger: event.trigger ?? pending.trigger,
    round: Number(pending.round || 1),
    max_rounds: Number(event.max_rounds ?? pending.max_rounds ?? 2),
    questions,
    plan: event.plan || pending.plan,
    expires_at: event.expires_at ?? pending.expires_at ?? null,
  };
}

/**
 * A `research.clarify` that has no provisional question to upgrade.
 *
 * NOT every clarify event is an upgrade. The deterministic gate answers a send
 * with a provisional question in the 200 itself, and `research.clarify` then
 * replaces it — that is the R4 path, and it always has something to replace.
 *
 * Layer 2 asks the other way round. The send is ACCEPTED, the turn starts
 * running, and the engine decides mid-flight that it cannot proceed without an
 * answer — `L2_OUTPUT_FORMAT` asking whether the user wants a document or a
 * reply in chat is exactly this — so it holds the turn before retrieval and
 * emits the question with nothing on the client for it to land on. There is no
 * provisional because the gate never wrote one.
 *
 * So the event has to be able to CREATE the pending plan, not only amend one.
 * The round is whatever the engine says (1, for a first hold), and the layer
 * defaults to 2 because a model-authored question is what this path carries.
 */
export function pendingFromClarifyEvent(
  event: ClarifyEvent,
  fallback: { messageUuid?: string; correlationId?: string } = {}
): PendingPlan {
  const payload: any = event || {};
  return {
    status: AWAITING_CLARIFICATION,
    message_uuid: String(payload.message_uuid || payload.messageUuid || fallback.messageUuid || ''),
    correlation_id: String(
      payload.correlation_id || payload.correlationId || fallback.correlationId || ''
    ),
    sequence_number: payload.sequence_number ?? null,
    trigger: payload.trigger ?? null,
    round: Number(payload.round ?? 1),
    max_rounds: Number(payload.max_rounds ?? 2),
    questions: Array.isArray(payload.questions) ? payload.questions : [],
    answers: payload.answers || {},
    plan: payload.plan || {},
    query: payload.query || '',
    layer: Number(payload.layer ?? 2),
    expires_at: payload.expires_at ?? null,
  };
}

/**
 * Is this the question set that is already on the turn?
 *
 * The difference between a REDELIVERY and a SECOND HOLD, and getting it wrong
 * costs the user either way: treat a second hold as a redelivery and the card
 * never appears (the turn sits there polling for a result that is waiting on a
 * question nobody was shown); treat a redelivery as a second hold and a
 * reconnect reopens a question the user has already answered.
 *
 * Identity is the question ids plus the round. An upgrade rewrites prose and
 * options against the SAME ids at the same round, so it is correctly the same
 * set; a genuinely new hold asks about different slots, or the same ones again
 * at a later round.
 */
export function sameQuestionSet(
  a: PendingPlan | null | undefined,
  b: PendingPlan | null | undefined
): boolean {
  if (!a || !b) return false;
  if (Number(a.round ?? 1) !== Number(b.round ?? 1)) return false;
  const ids = (plan: PendingPlan) =>
    (plan.questions || []).map((question) => question.id).sort().join('|');
  return ids(a) === ids(b);
}

/**
 * The draft, carried across an upgrade.
 *
 * What the user already typed or picked SURVIVES where the question id
 * survives — the upgrade rewrote the prose, not the slot, and discarding a
 * half-typed answer because a better question arrived would be the upgrade
 * costing the user work. Ids that are gone are dropped; new ones take their
 * defaults.
 */
export function upgradedDraft(
  previous: Record<string, string>,
  upgraded: PendingPlan
): Record<string, string> {
  const ids = new Set((upgraded.questions || []).map((question) => question.id));
  const kept: Record<string, string> = {};
  for (const [id, value] of Object.entries(previous || {})) {
    if (ids.has(id) && String(value ?? '').trim()) kept[id] = value;
  }
  return { ...initialDraft(upgraded), ...kept };
}

/* ------------------------------------------------------------------ */
/*  What the /answers endpoint said                                    */
/* ------------------------------------------------------------------ */

export type ClarifyOutcome = 'running' | 'question' | 'not_started';

/**
 * Read the resume response, by STATUS CODE first.
 *
 * This is the contract the server hardening pinned, and reading it wrong is
 * how a running turn came to be drawn as a failed one:
 *
 *   202 — the turn is going. `enqueued` and `already_running` are both 202
 *         and mean the same thing to this UI: subscribe to the correlation id
 *         and show the run starting. `already_running` is an ATTACH, not a
 *         refusal — a second submit of an answer that already took must never
 *         look like a failure.
 *   200 with `awaiting_clarification` and questions — round two.
 *   409 — and ONLY 409 — is a turn that genuinely never started.
 *
 * The `enqueued` flag is honoured when there is no status code to read (an
 * older response, or a test double), but it is the fallback, not the rule.
 */
export function classifyAnswerResult(result: any): ClarifyOutcome {
  const status = Number(result?.http_status ?? 0);
  if (status === 202) return 'running';
  if (status === 409) return 'not_started';

  const asked =
    String(result?.status || '') === AWAITING_CLARIFICATION &&
    Array.isArray(result?.questions) &&
    result.questions.length > 0;
  if (status === 200) return asked ? 'question' : 'running';

  // No status code on the response. Fall back to the body's own claims.
  if (result?.enqueued || result?.already_running) return 'running';
  return asked ? 'question' : 'not_started';
}

/**
 * The correlation to subscribe to once the answer took.
 *
 * A resumed turn keeps its correlation id, so the response usually echoes the
 * one the card already had — but `already_running` may name the correlation of
 * the run that beat this submit, and THAT is the one carrying the events.
 */
export function resumedCorrelationId(
  result: any,
  pending: PendingPlan
): string {
  return String(result?.correlation_id || pending.correlation_id || '');
}
